import React, { FunctionComponent } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';

import { LazyImage } from '../../LazyImage/LazyImage';
import { ImageSizeType } from '../../types/ImageSizeType';
import styles from '../styles.scss';

import { getSrcSetString } from '../../helpers/srcSet';
import { ThumbImage_item as ThumbImageItemProp } from './__generated__/ThumbImage_item.graphql';

export type ImageProps = {
    disableLinks?: boolean;
    onClick?: () => void;
    pdpUrl: string;
    title: string;
    containerClassName?: string;
    srcSetSizes?: string;
    srcSetWidths?: Array<number>;
    srcSetQuality?: number;
    useLoFiLazyLoader: boolean;
    imageSize: ImageSizeType;
    imageLoadVerticalOffset?: number;
    onContentLoaded?: () => void;
};

type Props = ImageProps & {
    item: ThumbImageItemProp;
    index: number;
    useEagerImageLoad: boolean;
};

export const ThumbImageComponent: FunctionComponent<Props> = ({
    item,
    index,
    useEagerImageLoad,
    containerClassName,
    disableLinks,
    onClick,
    pdpUrl,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    imageSize,
    title,
    imageLoadVerticalOffset,
    onContentLoaded,
    useLoFiLazyLoader,
}) => {
    const smallPath = get(item, 'smallPath');
    const placeholder = get(item, 'placeholder');
    const masterPath = get(item, 'masterOrZoomPath') || '';

    const imgSource = smallPath || masterPath;

    const srcSetAttributes =
        srcSetSizes && masterPath
            ? {
                  srcSet: getSrcSetString(masterPath, {
                      widths: srcSetWidths,
                      quality: srcSetQuality,
                  }),
                  sizes: srcSetSizes,
              }
            : {};

    const image = (
        <div className={containerClassName} data-tn="product-image-container">
            {useEagerImageLoad ? (
                <img
                    className={styles.swiperImage}
                    alt={title}
                    data-tn="product-image"
                    {...srcSetAttributes}
                    src={imgSource}
                />
            ) : (
                <React.Fragment>
                    <LazyImage
                        className={styles.swiperImage}
                        srcSetAttributes={srcSetAttributes}
                        placeholder={placeholder}
                        alt={title}
                        offsetVertical={imageLoadVerticalOffset}
                        onImageLoad={onContentLoaded}
                        useLoFiLazyLoader={useLoFiLazyLoader}
                        src={imgSource}
                        imageSize={imageSize}
                    />
                    {index === 0 && (
                        <noscript>
                            <img
                                className={styles.swiperImage}
                                alt={title}
                                {...srcSetAttributes}
                                src={imgSource}
                            />
                        </noscript>
                    )}
                </React.Fragment>
            )}
        </div>
    );

    const imageWithLink = (
        <a href={pdpUrl} onClick={onClick}>
            {image}
        </a>
    );

    return disableLinks ? image : imageWithLink;
};

export const ThumbImage = createFragmentContainer(ThumbImageComponent, {
    item: graphql`
        fragment ThumbImage_item on ItemPhoto {
            placeholder
            smallPath
            masterOrZoomPath
        }
    `,
});
